import { ChevronRight } from 'lucide-react'
import type { HTMLAttributes, ReactNode } from 'react'

/**
 * A titled, collapsible section used by the example gallery and the rule list.
 * The caret rotates via CSS; the body simply unmounts when collapsed. `right`
 * renders controls (e.g. a count badge or a bulk toggle) on the header's right.
 *
 * `handle` renders a leading slot before the caret (e.g. a drag grip), and
 * `headProps` is spread onto the header element so callers can make the section
 * draggable (draggable + onDragStart/Over/Drop). `className` augments the root,
 * letting callers add drag-state styling hooks (e.g. dragging / drag-over).
 */
export function CollapsibleGroup({
  title,
  collapsed,
  onToggle,
  right,
  handle,
  headProps,
  className,
  children,
}: {
  title: ReactNode
  collapsed: boolean
  onToggle: () => void
  right?: ReactNode
  handle?: ReactNode
  headProps?: HTMLAttributes<HTMLDivElement>
  className?: string
  children: ReactNode
}) {
  return (
    <div className={'collapsible' + (collapsed ? ' collapsed' : '') + (className ? ' ' + className : '')}>
      <div className="collapsible-head" {...headProps}>
        {handle}
        <button
          className="collapsible-toggle"
          onClick={onToggle}
          aria-expanded={!collapsed}
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          <ChevronRight size={14} className="collapsible-caret" />
          <span className="collapsible-title">{title}</span>
        </button>
        {right && <div className="collapsible-right">{right}</div>}
      </div>
      {!collapsed && <div className="collapsible-body">{children}</div>}
    </div>
  )
}
